import { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

const PKG_NAMES: Record<string, string> = {
  starter: 'Starter Website',
  business: 'Business Website',
  growth: 'Growth Package',
  maintenance: 'Website Maintenance',
  google_business: 'Google Business Setup',
};

const NEXT_STEPS = [
  { icon: 'ri-mail-send-line', step: 'Check your email', note: 'Confirmation receipt from Stripe' },
  { icon: 'ri-phone-line', step: "We'll reach out", note: 'Within 24 hours to get started' },
  { icon: 'ri-rocket-line', step: 'Go live', note: 'Your project is underway!' },
];

const TEMPLATE_ACCESS_FORM = 'https://readdy.ai/api/form/d7f7t2p5vm7vckkodvo0';

// ── Test Panel Component ──────────────────────────────────
function TestEmailPanel() {
  const [testEmail, setTestEmail] = useState('');
  const [testName, setTestName] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const handleSend = async () => {
    if (!testEmail) return;
    setStatus('sending');
    const reddyEditUrl = 'https://readdy.ai';
    const data = new URLSearchParams({
      template_name: 'ARK/8 (Test)',
      email: testEmail,
      customer_name: testName || 'Test User',
      template_id: 'test-001',
      readdy_edit_url: reddyEditUrl,
      message: `You now have full access to the "ARK/8 (Test)" template. Click the link below to start editing your template on Readdy: ${reddyEditUrl}`,
    });
    try {
      await fetch(TEMPLATE_ACCESS_FORM, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="w-full max-w-lg mx-auto mb-10 border-2 border-dashed border-amber-400 rounded-2xl p-5 bg-amber-50">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 flex items-center justify-center bg-amber-400 rounded-full">
          <i className="ri-test-tube-line text-white text-xs"></i>
        </div>
        <span className="text-amber-700 text-xs font-black uppercase tracking-widest">Test Mode — Email Trigger</span>
      </div>
      <p className="text-amber-600 text-xs mb-4 leading-relaxed">
        Enter your email below and hit <strong>Send Test Email</strong> to simulate what a paying customer receives after purchase.
      </p>
      <div className="flex flex-col gap-2 mb-3">
        <input
          type="text"
          placeholder="Your name (optional)"
          value={testName}
          onChange={e => setTestName(e.target.value)}
          className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm text-black bg-white focus:outline-none focus:border-amber-500"
        />
        <input
          type="email"
          placeholder="your@email.com"
          value={testEmail}
          onChange={e => setTestEmail(e.target.value)}
          className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm text-black bg-white focus:outline-none focus:border-amber-500"
        />
      </div>
      <button
        onClick={handleSend}
        disabled={!testEmail || status === 'sending' || status === 'sent'}
        className="w-full py-2.5 bg-amber-400 hover:bg-amber-500 disabled:opacity-50 text-white text-sm font-bold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
      >
        {status === 'idle' && <><i className="ri-send-plane-line mr-1.5"></i>Send Test Email</>}
        {status === 'sending' && <><i className="ri-loader-4-line mr-1.5 animate-spin"></i>Sending...</>}
        {status === 'sent' && <><i className="ri-check-line mr-1.5"></i>Email Sent! Check your inbox</>}
        {status === 'error' && <><i className="ri-error-warning-line mr-1.5"></i>Error — try again</>}
      </button>
      {status === 'sent' && (
        <p className="text-green-600 text-xs mt-2 text-center font-medium">
          Form submitted successfully. Check <strong>{testEmail}</strong> for the access email.
        </p>
      )}
    </div>
  );
}

export default function PaySuccessPage() {
  const [searchParams] = useSearchParams();
  const pkg = searchParams.get('pkg') || '';
  const templateName = searchParams.get('template') || '';
  const customerEmail = searchParams.get('email') || '';
  const customerName = searchParams.get('name') || '';
  const templateId = searchParams.get('tid') || '';
  const [visible, setVisible] = useState(false);
  const emailSentRef = useRef(false);

  const isTemplatePurchase = !!templateName;

  // Auto-send access email for template purchases
  useEffect(() => {
    if (!isTemplatePurchase || emailSentRef.current) return;
    emailSentRef.current = true;

    const reddyEditUrl = 'https://readdy.ai';
    const data = new URLSearchParams({
      template_name: templateName,
      email: customerEmail,
      customer_name: customerName,
      template_id: templateId,
      readdy_edit_url: reddyEditUrl,
      message: `You now have full access to the "${templateName}" template. Click the link below to start editing your template on Readdy: ${reddyEditUrl}`,
    });

    fetch(TEMPLATE_ACCESS_FORM, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    }).catch(() => {});
  }, [isTemplatePurchase, templateName, customerEmail, customerName, templateId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  // ── Template purchase success ──────────────────────────
  if (isTemplatePurchase) {
    return (
      <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'Inter', sans-serif" }}>
        <header className="border-b border-black/10 px-5 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className="text-black font-black text-base tracking-widest uppercase">Nova AI Digital</span>
          </Link>
        </header>

        <main className="flex-1 flex items-center justify-center px-5 py-20">
          <div
            className="max-w-lg w-full text-center transition-all duration-700"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)' }}
          >
            {/* TEST PANEL */}
            <TestEmailPanel />

            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-black mx-auto mb-6">
              <i className="ri-check-line text-white text-3xl"></i>
            </div>

            <span className="text-black/40 text-xs tracking-[0.2em] uppercase font-medium">Payment Successful</span>
            <h1 className="text-4xl font-black text-black mt-2 leading-tight">
              You&apos;re unlocked!
            </h1>

            <div className="mt-4 inline-flex items-center gap-2 bg-black/5 px-5 py-2.5 rounded-full">
              <i className="ri-layout-line text-black text-sm"></i>
              <span className="text-black text-sm font-bold">{templateName}</span>
              <span className="text-black/40 text-sm">template</span>
            </div>

            <p className="text-black/50 text-sm mt-5 leading-relaxed max-w-sm mx-auto">
              {customerEmail
                ? <>A full access confirmation has been sent to <strong className="text-black">{customerEmail}</strong>. Check your inbox for your template access link and instructions to edit on Readdy.</>
                : <>Your full access confirmation is on its way to your inbox. Check your email for your template access link and instructions to edit on Readdy.</>
              }
            </p>

            {/* What's included */}
            <div className="mt-8 text-left border border-black/10 rounded-2xl p-6 flex flex-col gap-4">
              <p className="text-xs font-semibold text-black/40 uppercase tracking-widest">What you get</p>
              {[
                { icon: 'ri-mail-check-line', title: 'Access email sent', note: 'Check your inbox for your template link' },
                { icon: 'ri-edit-line', title: 'Edit on Readdy', note: 'Customize your template directly in Readdy' },
                { icon: 'ri-refresh-line', title: 'Monthly access', note: 'Active as long as your subscription is live' },
                { icon: 'ri-customer-service-line', title: 'We can build it for you', note: 'Contact us anytime to have us build it' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-black/5 flex-shrink-0">
                    <i className={`${item.icon} text-black text-sm`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-black">{item.title}</p>
                    <p className="text-xs text-black/40">{item.note}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="https://readdy.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-7 py-3.5 bg-black text-white text-sm font-bold rounded-full hover:bg-black/80 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-edit-line"></i>
                Edit Template on Readdy
              </a>
              <Link
                to="/"
                className="flex items-center justify-center gap-2 px-7 py-3.5 border border-black/20 text-black text-sm font-semibold rounded-full hover:bg-black hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-line"></i>
                Back to Templates
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ── Regular purchase success (existing) ───────────────
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Nav */}
      <header className="border-b border-white/10 px-5 md:px-12 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <div className="w-7 h-7 flex items-center justify-center">
            <i className="ri-flashlight-fill text-orange-400 text-xl" />
          </div>
          <span className="text-white font-black text-base tracking-widest uppercase">Nova AI</span>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-20">
        <div
          className="max-w-lg w-full text-center transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)' }}
        >
          <div className="w-20 h-20 flex items-center justify-center border border-orange-400/40 mx-auto mb-8">
            <i className="ri-check-line text-orange-400 text-4xl" />
          </div>

          <span className="text-white/40 text-xs tracking-[0.25em] uppercase font-medium">Payment Successful</span>
          <h1 className="text-4xl md:text-5xl font-black text-white mt-3 leading-tight">
            You&apos;re all set!
          </h1>

          {pkg && PKG_NAMES[pkg] && (
            <div className="mt-6 inline-flex items-center gap-2 border border-orange-400/30 bg-orange-400/5 px-5 py-3">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-flashlight-fill text-orange-400 text-xs" />
              </div>
              <span className="text-white text-sm font-bold">{PKG_NAMES[pkg]}</span>
              <span className="text-white/40 text-sm">confirmed</span>
            </div>
          )}

          <p className="text-white/40 text-sm mt-6 leading-relaxed max-w-sm mx-auto">
            A confirmation email is on its way to your inbox. We&apos;ll be in touch within 24 hours to kick things off. Welcome to the team!
          </p>

          <div className="mt-10 text-left border border-white/10 p-6">
            <p className="text-white/40 text-xs tracking-widest uppercase mb-4">What Happens Next</p>
            <div className="flex flex-col gap-4">
              {NEXT_STEPS.map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center border border-white/20 flex-shrink-0">
                    <i className={`${item.icon} text-white/50 text-sm`} />
                  </div>
                  <div>
                    <p className="text-white text-sm font-semibold">{item.step}</p>
                    <p className="text-white/30 text-xs">{item.note}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/"
              className="px-7 py-3.5 bg-white text-black text-sm font-black tracking-widest uppercase hover:bg-orange-400 hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer text-center"
            >
              Back to Home
            </Link>
            <a
              href="tel:+19512909947"
              className="px-7 py-3.5 border border-white/30 text-white text-sm font-bold tracking-widest uppercase hover:bg-white hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer text-center"
            >
              Call Us
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
